export const RANGE_FINDER_TIMEFRAMES = [
  "15s",
  "30s",
  "45s",
  "1m",
  "2m",
  "3m",
  "4m",
  "6m",
  "8m",
  "12m",
  "16m",
  "23m",
  "32m",
  "45m",
  "64m",
  "90m",
  "2H",
  "3H",
  "4H",
];

export const RANGE_FINDER_MULTIPLIERS = ["x2", "x4", "x8"];
export const RANGE_FINDER_WAVES = [
  "U1",
  "U2",
  "U3",
  "U4",
  "U5",
  "D1",
  "D2",
  "D3",
  "D4",
  "D5",
];

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

export const parseTimeframeToSeconds = (timeframe) => {
  const normalized = String(timeframe || "").trim();
  const match = normalized.match(/^(\d+(?:\.\d+)?)(s|m|min|h|H|d|D)$/);
  if (!match) {
    throw new Error(`Unsupported timeframe: ${timeframe}`);
  }

  const value = Number.parseFloat(match[1]);
  const unit = match[2].toLowerCase();
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`Unsupported timeframe: ${timeframe}`);
  }

  if (unit === "s") return value;
  if (unit === "m" || unit === "min") return value * SECONDS_PER_MINUTE;
  if (unit === "h") return value * SECONDS_PER_HOUR;
  if (unit === "d") return value * SECONDS_PER_DAY;

  throw new Error(`Unsupported timeframe: ${timeframe}`);
};

export const RANGE_FINDER_REFERENCE_TIMEFRAMES = [
  ...RANGE_FINDER_TIMEFRAMES,
  "6H",
  "8H",
  "12H",
  "16H",
  "1D",
].sort((a, b) => parseTimeframeToSeconds(a) - parseTimeframeToSeconds(b));

export const findClosestTimeframe = (
  targetSeconds,
  options = RANGE_FINDER_TIMEFRAMES,
) => {
  if (!Number.isFinite(targetSeconds) || targetSeconds <= 0) {
    throw new Error(`Unsupported target timeframe seconds: ${targetSeconds}`);
  }

  return options.reduce((best, option) => {
    const optionSeconds = parseTimeframeToSeconds(option);
    if (!best) {
      return { timeframe: option, seconds: optionSeconds };
    }

    const bestDistance = Math.abs(best.seconds - targetSeconds);
    const optionDistance = Math.abs(optionSeconds - targetSeconds);
    if (optionDistance < bestDistance) {
      return { timeframe: option, seconds: optionSeconds };
    }
    if (optionDistance === bestDistance && optionSeconds > best.seconds) {
      return { timeframe: option, seconds: optionSeconds };
    }
    return best;
  }, null).timeframe;
};

export const buildRangeFinderGrid = (
  referenceTimeframe,
  options = RANGE_FINDER_TIMEFRAMES,
) => {
  const referenceSeconds = parseTimeframeToSeconds(referenceTimeframe);

  return RANGE_FINDER_MULTIPLIERS.map((multiplier, index) => {
    const divisor = 2 ** (index + 1);
    const optimalTargetSeconds = referenceSeconds / divisor;
    const tacticalTargetSeconds = referenceSeconds / (divisor * 2);
    const optimalTimeframe = findClosestTimeframe(optimalTargetSeconds, options);
    const tacticalTimeframe = findClosestTimeframe(tacticalTargetSeconds, options);

    return {
      key: multiplier,
      multiplier,
      optimal: {
        row: "optimal",
        timeframe: optimalTimeframe,
        targetSeconds: optimalTargetSeconds,
        seconds: parseTimeframeToSeconds(optimalTimeframe),
        isExact: parseTimeframeToSeconds(optimalTimeframe) === optimalTargetSeconds,
      },
      tactical: {
        row: "tactical",
        timeframe: tacticalTimeframe,
        targetSeconds: tacticalTargetSeconds,
        seconds: parseTimeframeToSeconds(tacticalTimeframe),
        isExact: parseTimeframeToSeconds(tacticalTimeframe) === tacticalTargetSeconds,
      },
    };
  });
};

export const getTrackedRangeFinderTimeframes = (grid) => {
  const seen = new Set();
  const ordered = [];

  grid.forEach((column) => {
    [column.optimal.timeframe, column.tactical.timeframe].forEach((timeframe) => {
      if (!seen.has(timeframe)) {
        seen.add(timeframe);
        ordered.push(timeframe);
      }
    });
  });

  return ordered;
};

export const normalizeWave = (wave) => {
  const normalized = String(wave || "").trim().toUpperCase();
  return RANGE_FINDER_WAVES.includes(normalized) ? normalized : null;
};

export const getWaveDirection = (wave) => {
  const normalized = normalizeWave(wave);
  if (!normalized) return null;

  const cycle = normalized[0];
  const number = Number.parseInt(normalized.slice(1), 10);
  const isOdd = number % 2 === 1;

  if (cycle === "U") {
    return isOdd ? "buy" : "sell";
  }
  return isOdd ? "sell" : "buy";
};

export const getCycleDirection = (wave) => {
  const normalized = normalizeWave(wave);
  if (!normalized) return null;
  return normalized[0] === "U" ? "buy" : "sell";
};

export const getWaveForTimeframe = (waveState, timeframe) => {
  const value = waveState?.[timeframe];
  if (typeof value === "string") return normalizeWave(value);
  return normalizeWave(value?.currentWave);
};

const isHistoryMatch = (value) =>
  value === true || value === "match" || value === "ok";

export const countRecentMatches = (history = []) =>
  history.slice(0, 3).filter(isHistoryMatch).length;

export const scoreRangeFinderPairs = (
  grid,
  waveState = {},
  pairHistory = {},
) =>
  grid.map((column, index) => {
    const seniorWave = getWaveForTimeframe(waveState, column.optimal.timeframe);
    const juniorWave = getWaveForTimeframe(waveState, column.tactical.timeframe);
    const seniorWaveDirection = getWaveDirection(seniorWave);
    const juniorCycleDirection = getCycleDirection(juniorWave);
    const isCurrentMatch = Boolean(
      seniorWaveDirection &&
      juniorCycleDirection &&
      seniorWaveDirection === juniorCycleDirection,
    );
    const recentMatches = countRecentMatches(pairHistory[column.key] || []);

    return {
      ...column,
      index,
      seniorWave,
      juniorWave,
      seniorWaveDirection,
      juniorCycleDirection,
      isCurrentMatch,
      recentMatches,
      score: (isCurrentMatch ? 100 : 0) + recentMatches,
    };
  });

export const selectBestRangeFinderPair = (
  grid,
  waveState = {},
  pairHistory = {},
) => {
  const scoredPairs = scoreRangeFinderPairs(grid, waveState, pairHistory);
  const currentMatches = scoredPairs.filter((pair) => pair.isCurrentMatch);
  if (currentMatches.length === 0) {
    return { bestPair: null, scoredPairs };
  }

  const bestPair = [...currentMatches].sort((a, b) => {
    if (b.recentMatches !== a.recentMatches) {
      return b.recentMatches - a.recentMatches;
    }
    return a.index - b.index;
  })[0];

  return { bestPair, scoredPairs };
};

export const createRangeFinderConfig = (referenceTimeframe = "8H") => ({
  referenceTimeframe,
  waves: {},
  pairHistory: {
    x2: [],
    x4: [],
    x8: [],
  },
});

export const safeBuildRangeFinderModel = (config) => {
  try {
    const safeConfig = config || createRangeFinderConfig();
    const grid = buildRangeFinderGrid(safeConfig.referenceTimeframe || "8H");
    const trackedTimeframes = getTrackedRangeFinderTimeframes(grid);
    const { bestPair, scoredPairs } = selectBestRangeFinderPair(
      grid,
      safeConfig.waves || {},
      safeConfig.pairHistory || {},
    );

    return {
      error: null,
      grid,
      trackedTimeframes,
      bestPair,
      scoredPairs,
    };
  } catch (error) {
    return {
      error,
      grid: [],
      trackedTimeframes: [],
      bestPair: null,
      scoredPairs: [],
    };
  }
};
